// ===== FLOOR / UNIT SELECTION -> RESIDENTS =====
//
// One place for what the WinForms report dialogs do with the Floor/Unit
// checklist (ucDietech_Report_Generator_ClbFloorUnit.vb). The checklist has two
// shapes, picked by the facility's UseUnitMapping setting (.vb:46, :71-75):
//
//   UseUnitMapping = True   one row per tblUnit_Mapping row, sorted Floor, Wing
//                           (Fill_dtUnitMapping .vb:134-152); the value is
//                           PKey_Unit_Mapping, the label is Unit.
//   UseUnitMapping = False  one row per distinct Floor of the facility's
//                           non-deleted tblPatient rows (Fill_dtFloors
//                           .vb:105-132); the value is CInt(Floor) and the
//                           label is the floor (GetCheckedFloorUnits .vb:213-229).
//
// So the numbers a report sends as "the selected floors/units" are unit keys on
// one kind of facility and FLOOR NUMBERS on the other. The generators compare
// them that way too (DishUpLabels.vb:180-201), so sending unit keys from a
// floor facility filters out everyone.
//
// A resident is "in" the selection when (IsFloorWingUnitSelected .vb:174-192):
//   unit mapping  a checked unit row has the resident's Floor AND Wing;
//   floors        the resident's floor number is checked.
//
// Everything here is pure; callers pass the facility's units and residents.

/** A tblUnit_Mapping row - the columns the checklist reads. */
export interface FloorUnitRow {
  pKey_Unit_Mapping: number;
  floor: string | null;
  wing: string | null;
  unit?: string | null;
  unit_Name?: string | null;
}

/** A resident's location - the columns the floor/unit match reads. */
export interface LocatedResident {
  pKey_Patient: number;
  floor: string | null;
  wing: string | null;
}

export interface FloorUnitOption {
  value: number;
  label: string;
}

/** WinForms `CInt(Floor)`: the floor column is text, the checklist value is an
 *  integer. Blank or non-numeric floors cannot be checked (CInt throws on them in
 *  WinForms), so they map to null. CInt rounds; floors are whole numbers in
 *  practice, so a fractional floor is treated as not a floor. */
export function floorNumber(floor: string | null | undefined): number | null {
  const s = (floor ?? '').trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isInteger(n) ? n : null;
}

/** SQL Server compares Floor / Wing case-insensitively and ignores trailing
 *  blanks (the joins and DataTable.Select WinForms uses), so do the same. */
function sameText(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trimEnd().toLowerCase() === (b ?? '').trimEnd().toLowerCase();
}

function sortUnits<T extends FloorUnitRow>(units: readonly T[]): T[] {
  const norm = (s: string | null | undefined) => (s ?? '').toLocaleLowerCase();
  return [...units].sort((a, b) => {
    const fa = norm(a.floor), fb = norm(b.floor);
    if (fa !== fb) return fa < fb ? -1 : 1;
    const wa = norm(a.wing), wb = norm(b.wing);
    if (wa !== wb) return wa < wb ? -1 : 1;
    return 0;
  });
}

/**
 * The checklist rows, as WinForms fills them.
 *
 * `residents` is only read on a floor facility: the facility's non-deleted
 * residents (discharged included - Fill_dtFloors filters on IsDeleted only).
 */
export function floorUnitOptions(
  useUnitMapping: boolean,
  units: readonly FloorUnitRow[],
  residents: readonly { floor: string | null; isDeleted?: boolean }[],
): FloorUnitOption[] {
  if (useUnitMapping) {
    return sortUnits(units).map((u) => ({
      value: u.pKey_Unit_Mapping,
      label: u.unit || u.unit_Name || `Unit ${u.pKey_Unit_Mapping}`,
    }));
  }
  const byNumber = new Map<number, string>();
  for (const r of residents) {
    if (r.isDeleted) continue;
    const n = floorNumber(r.floor);
    if (n == null || byNumber.has(n)) continue;
    byNumber.set(n, (r.floor ?? '').trim());
  }
  return [...byNumber.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([value, label]) => ({ value, label }));
}

/** WinForms `IsFloorWingUnitSelected` (ClbFloorUnit.vb:174-192). */
export function isResidentInFloorUnits(
  resident: { floor: string | null; wing: string | null },
  selected: ReadonlySet<number>,
  useUnitMapping: boolean,
  units: readonly FloorUnitRow[],
): boolean {
  if (useUnitMapping) {
    return units.some((u) =>
      selected.has(u.pKey_Unit_Mapping)
      && sameText(u.floor, resident.floor)
      && sameText(u.wing, resident.wing));
  }
  const n = floorNumber(resident.floor);
  return n != null && selected.has(n);
}

/**
 * The residents in the selected floors/units, in `residents` order. Mirrors the
 * loops in the WinForms dialogs that turn the checklist into a PatientList
 * (TrayTicket.vb:781-799 / :494-511, MedPassSummary.vb:234-259,
 * ManagementTeam.vb:1001-1012).
 */
export function residentsInFloorUnits(
  residents: readonly LocatedResident[],
  selected: readonly number[],
  useUnitMapping: boolean,
  units: readonly FloorUnitRow[],
): number[] {
  const set = new Set(selected);
  return residents
    .filter((r) => isResidentInFloorUnits(r, set, useUnitMapping, units))
    .map((r) => r.pKey_Patient);
}

/** Display names for the selection - Tray Tickets' LstFloorUnitNames
 *  (TrayTicket.vb:801-807): the Unit column on a unit-mapping facility, the
 *  floor number on a floor facility. */
export function floorUnitNames(
  selected: readonly number[],
  useUnitMapping: boolean,
  units: readonly FloorUnitRow[],
): string[] {
  if (!useUnitMapping) return selected.map((n) => String(n));
  const byKey = new Map(units.map((u) => [u.pKey_Unit_Mapping, u]));
  return selected.map((pk) => {
    const u = byKey.get(pk);
    return u ? (u.unit ?? '') : String(pk);
  });
}

/**
 * WinForms `GetCheckedFloorWing_CommaSep` (ClbFloorUnit.vb:244-260): "Floor,Wing"
 * per checked unit, or "Floor,*" per checked floor. The floor text is the
 * residents' own (so a floor stored as "02" is sent as "02"), falling back to
 * the number when no resident carries it.
 */
export function floorWingStrings(
  selected: readonly number[],
  useUnitMapping: boolean,
  units: readonly FloorUnitRow[],
  residents: readonly { floor: string | null; isDeleted?: boolean }[],
): string[] {
  if (useUnitMapping) {
    const set = new Set(selected);
    return sortUnits(units)
      .filter((u) => set.has(u.pKey_Unit_Mapping))
      .map((u) => `${u.floor ?? ''},${u.wing ?? ''}`);
  }
  const out: string[] = [];
  // Checklist order (floors ascending), as WinForms walks the list.
  for (const n of [...selected].sort((a, b) => a - b)) {
    const texts = new Set<string>();
    for (const r of residents) {
      if (r.isDeleted) continue;
      if (floorNumber(r.floor) === n) texts.add((r.floor ?? '').trim());
    }
    if (texts.size === 0) texts.add(String(n));
    for (const t of texts) out.push(`${t},*`);
  }
  return out;
}
